// GeminiService.js
import axios from 'axios';

// Simple client-side rate limit and retry for 429 resilience
let clientNextTime = 0;
const CLIENT_MIN_GAP = 700; // ms between client calls (slightly higher to avoid 429s)
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const clientCache = new Map(); // key -> { text, expires }
const CLIENT_CACHE_TTL = 30_000; // 30s

function cacheGet(key) {
    const e = clientCache.get(key);
    if (!e) return null;
    if (Date.now() > e.expires) { clientCache.delete(key); return null; }
    return e.text;
}
function cacheSet(key, text) {
    clientCache.set(key, { text, expires: Date.now() + CLIENT_CACHE_TTL });
}

async function limitedPost(url, body, config = {}) {
    const now = Date.now();
    const jitter = Math.floor(Math.random() * 160);
    const start = Math.max(now, clientNextTime);
    clientNextTime = start + CLIENT_MIN_GAP + jitter;
    const wait = start - now;
    if (wait > 0) await sleep(wait);

    let lastErr;
    for (let attempt = 0; attempt < 3; attempt++) {
        try {
            return await axios.post(url, body, config);
        } catch (err) {
            lastErr = err;
            const status = err.response?.status;
            if (status === 429 || (status >= 500 && status < 600)) {
                await sleep(350 * (attempt + 1));
                continue;
            }
            throw err;
        }
    }
    throw lastErr;
}

const SYSTEM_INSTRUCTION = [
    'You are an expert resume writer and editor.',
    'STRICT RULES: Output must be valid JSON ONLY that matches response_schema. Do not include any explanations, advice, prompts, or meta commentary.',
    'CONTENT RULES: Provide final, publish-ready resume content only. No second-person (you/your). Avoid first-person unless the field explicitly allows it (coverLetter). No placeholders like [text] or (text). No generic filler (passionate, hardworking) unless grounded. No references to resume/ATS/section/prompt/model.',
    'STYLE RULES: Concise, professional, US spelling. No section headers inside content (e.g., "Summary:"). Keep bullets tight and results-focused; avoid trailing periods in skill bullets.',
].join(' ');

// Heuristics to detect or remove meta commentary/advice/placeholder content
const META_PATTERNS = [
    /\b(to align|reframe|consider|you should|you can|you could|should|might|could|for example|e\.g\.|add specific|translate[d]? to)\b/i,
    /\b(resume|ATS|this section|this content|prompt|model|schema|JSON)\b/i,
    /\b(transferable skills include|to adapt (this|it)|can be framed as|can be related to|can be seen as|emphasize|highlight instances|consider adding)\b/i,
    /\b(while your current|in other words|the takeaway is)\b/i,
    /\b(professional experience\s*[:-]?)\b/i,
    /\[[^\]]+\]/, // bracket placeholders
    /\b(as an? .*?,|i am (?:excited|eager) to)\b/i, // cover-letter-like advice in non-CL fields
];

function hasMeta(text) {
    if (!text) return false;
    return META_PATTERNS.some((re) => re.test(text));
}

function stripPlaceholders(text) {
    if (!text) return '';
    return text
        .replace(/\[[^\]]+\]/g, '')
    .replace(/\([^)]*\)/g, '')
        .replace(/[“”]/g, '"')
        .replace(/[‘’]/g, "'");
}

function titleCase(str) {
    return (str || '')
        .toLowerCase()
        .split(/\s+/)
        .map((w) => w.replace(/^([a-z])/, (m) => m.toUpperCase()))
        .join(' ');
}

function sanitizeForField(field, value) {
    if (!value) return '';
    let out = stripPlaceholders(String(value)).trim();
    // Remove obvious labels
    out = out.replace(/^\s*(summary|experience|skills|education|publications|title)\s*:\s*/i, '');

    if (field === 'title') {
        // Keep 3–7 words, title case, no quotes; allow one hyphen
    out = out.replace(/[".!?;:,]+/g, ' ').replace(/\s+/g, ' ').trim();
        const words = out.split(' ').filter(Boolean).slice(0, 7);
        out = titleCase(words.join(' '));
        return out;
    }

    if (field === 'coverLetter') {
        // Cover letter can be first-person. Remove meta commentary but keep paragraphs.
        let cleaned = out
            .replace(/\b(ATS|resume|this section|schema|JSON|prompt|model)\b/gi, '')
            .replace(/\s+/g, ' ')
            .trim();
        return cleaned;
    }

    // For bullet-style fields we return newline-separated bullets string
    const bulletFields = new Set(['experience', 'skills', 'education', 'publications']);
    if (bulletFields.has(field)) {
    // Split on explicit newlines or bullet characters, but NOT plain hyphens which can appear in phrases
    const lines = out.split(/\r?\n|\u2022|•/).map((l) => l.trim()).filter(Boolean);
        const cleaned = lines
            .filter((l) => !hasMeta(l))
            .map((l) => l.replace(/^[•-]\s*/, ''))
            .map((l) => l.replace(/\.+$/, ''))
            .map((l) => l.replace(/\s+/g, ' '))
            .map((l) => l.length > 160 ? l.slice(0, 160).trim() : l)
            .filter((l) => l && /[A-Za-z]/.test(l));
        // De-duplicate, cap to 6 bullets
        const uniq = Array.from(new Set(cleaned)).slice(0, 6);
        return uniq.join('\n');
    }

    // Default text: drop meta sentences and tidy
    const sentences = out.split(/(?<=[.!?])\s+/);
    const kept = sentences.filter((s) => !hasMeta(s)).map((s) => s.replace(/["\u201C\u201D]/g, '').trim());
    return kept.join(' ').replace(/\s+/g, ' ').trim();
}

const getTargetTokens = (field) => {
    const tokenByField = {
        title: 60,
        summary: 320,
        experience: 420,
        skills: 180,
        education: 220,
        publications: 220,
    coverLetter: 900,
        default: 250,
    };
    return tokenByField[field] || tokenByField.default;
};

function schemaForField(field) {
    switch (field) {
        case 'title':
            return { type: 'object', properties: { title: { type: 'string', description: 'A concise professional resume title (3–7 words). No meta commentary, no quotes, no punctuation other than optional hyphen.' } }, required: ['title'] };
        case 'summary':
            return { type: 'object', properties: { summary: { type: 'string', description: 'A 2–4 sentence professional resume summary focusing on impact and skills. No advice, no meta commentary, no placeholders.' } }, required: ['summary'] };
        case 'experience':
            return { type: 'object', properties: { bullets: { type: 'array', items: { type: 'string' }, description: '3–6 concise accomplishment bullets; start with action verbs; quantify results. No advice/meta, no placeholders.' } }, required: ['bullets'] };
        case 'skills':
            return { type: 'object', properties: { bullets: { type: 'array', items: { type: 'string' }, description: 'Core skills as short bullet lines (noun phrases). No sentences, no trailing periods, no advice/meta.' } }, required: ['bullets'] };
        case 'education':
            return { type: 'object', properties: { bullets: { type: 'array', items: { type: 'string' }, description: 'Education entries as short bullets. No advice/meta.' } }, required: ['bullets'] };
        case 'publications':
            return { type: 'object', properties: { bullets: { type: 'array', items: { type: 'string' }, description: 'Publications as bullets: Title — Outlet, Year (or relevant details). No advice/meta.' } }, required: ['bullets'] };
        case 'coverLetter':
            return { type: 'object', properties: { letter: { type: 'string', description: 'A professional, personalized cover letter under 400 words. No meta commentary about resumes/ATS/schema.' } }, required: ['letter'] };
        default:
            return { type: 'object', properties: { text: { type: 'string', description: `A concise rewrite of the ${field} section.` } }, required: ['text'] };
    }
}

function formatFromJson(field, obj) {
    if (!obj || typeof obj !== 'object') return '';
    if (field === 'title' && typeof obj.title === 'string') return obj.title;
    if (field === 'summary' && typeof obj.summary === 'string') return obj.summary;
    if (field === 'coverLetter' && typeof obj.letter === 'string') return obj.letter;
    if (Array.isArray(obj.bullets)) {
        // Join bullets into newline-separated list; UI renders plain text
        return obj.bullets.filter(Boolean).map(s => s.trim()).filter(Boolean).join('\n');
    }
    if (typeof obj.text === 'string') return obj.text;
    return '';
}

const GeminiService = {
    // Helper to call backend AI proxy with schema-first JSON mode
    callGeminiChat: async ({ model, messages, max_tokens, temperature, response_mime_type, response_schema, actionType }) => {
    const key = JSON.stringify({ model: model || 'gemini-3.1-flash-lite', messages, max_tokens, temperature, response_mime_type: response_mime_type || 'application/json', response_schema, actionType });
        const cached = cacheGet(key);
        if (cached) return cached;

        const { data } = await limitedPost('/api/ai/gemini/chat', {
            model: model || 'gemini-3.1-flash-lite',
            messages,
            max_tokens,
            temperature,
            response_mime_type: response_mime_type || 'application/json',
            response_schema,
            actionType
        });
        cacheSet(key, data);
        return data;
    },

    // Extract job meta (company, role, recipient, team) from raw job text
    extractJobMeta: async (jobText) => {
        if (!jobText || typeof jobText !== 'string' || jobText.trim().length < 20) return {};
        const response_schema = {
            type: 'object',
            properties: {
                company: { type: 'string', description: 'Company or organization name if explicitly mentioned.' },
                roleTitle: { type: 'string', description: 'Exact job title.' },
                recipientName: { type: 'string', description: 'Hiring manager or contact full name if present.' },
                teamOrDepartment: { type: 'string', description: 'Team or department mentioned (e.g., Engineering, Product).' },
                location: { type: 'string', description: 'Job location if present.' }
            }
        };
        try {
            const data = await GeminiService.callGeminiChat({
                model: 'gemini-3.1-flash-lite',
                messages: [
                    { role: 'system', content: SYSTEM_INSTRUCTION },
                    { role: 'user', content: `From the job post text below, extract fields. If a field is not present, use an empty string. Return JSON per schema only.\n\nJob Post Text:\n${jobText}` }
                ],
                max_tokens: 220,
                temperature: 0.05,
                response_mime_type: 'application/json',
                response_schema,
                actionType: 'cover_letter'
            });
            const raw = (data && data.choices && data.choices[0] && data.choices[0].message && data.choices[0].message.content) ? data.choices[0].message.content : '';
            try {
                const obj = typeof raw === 'string' ? JSON.parse(raw) : (raw || {});
                const out = {};
                if (obj.company && typeof obj.company === 'string') out.company = obj.company.trim();
                if (obj.roleTitle && typeof obj.roleTitle === 'string') out.roleTitle = obj.roleTitle.trim();
                if (obj.recipientName && typeof obj.recipientName === 'string') out.recipientName = obj.recipientName.trim();
                if (obj.teamOrDepartment && typeof obj.teamOrDepartment === 'string') out.teamOrDepartment = obj.teamOrDepartment.trim();
                if (obj.location && typeof obj.location === 'string') out.location = obj.location.trim();
                return out;
            } catch (e) {
                console.warn('[GeminiService] JSON parse failed (extractJobMeta)', (raw||'').slice(0,120));
                return {};
            }
        } catch (err) {
            console.warn('[GeminiService] extractJobMeta failed', err?.message);
            return {};
        }
    },

    // Keep text fallback utility (used by extract/detect helpers)
    generateWithFallback: async (prompt, config) => {
        const data = await GeminiService.callGeminiChat({
            model: config.model || 'gemini-3.1-flash-lite',
            messages: [
                { role: 'system', content: SYSTEM_INSTRUCTION },
                { role: 'user', content: prompt }
            ],
            max_tokens: config.maxTokens || 250,
            temperature: config.temperature ?? 0.2,
            response_mime_type: 'text/plain',
            actionType: config.actionType || 'resume_edit'
        });
        const content = (data && data.choices && data.choices[0] && data.choices[0].message && data.choices[0].message.content) ? data.choices[0].message.content : '';
        return (content || '').trim();
    },

    generateText: async (field, currentText, baseCharacterLimit, updateStatus) => {
        console.log(`Generating text for ${field} with current text:`, currentText);
        const adjustedCharacterLimit = (contentLength, baseLimit) => {
            return contentLength > 2000 ? baseLimit - 50 : baseLimit;
        };

        const characterLimit = adjustedCharacterLimit(currentText.length, baseCharacterLimit);

        let prompt;
        const targetTokens = getTargetTokens(field);
        switch (field) {
            case 'title':
                prompt = `Rewrite the resume title professionally (3–7 words, nouns/adjectives, optionally a hyphen). Return JSON only per schema. Limit to ${characterLimit} characters for the title value.\n\nContext:\n${currentText}`;
                break;
            case 'summary':
                prompt = `Rewrite and enhance the resume summary to 2–4 sentences, highlighting impact, skills, and scope. Limit to ${characterLimit} characters.\n\nSummary:\n${currentText}`;
                break;
            case 'experience':
                prompt = `Rewrite the experience content into 3–6 accomplishment bullets. Start with action verbs, quantify results, and keep each bullet concise. Do NOT include any explanations, examples, or section headers. Bullet lines only. Return JSON only per schema. Limit total to ${characterLimit} characters.\n\nExperience:\n${currentText}`;
                break;
            case 'skills':
                prompt = `Rewrite the skills into concise bullet lines grouped logically (noun phrases). No sentences, no trailing periods. Do NOT include any explanations or section headers. Bullet lines only. Return JSON only per schema. Limit total to ${characterLimit} characters.\n\nSkills:\n${currentText}`;
                break;
            case 'education':
                prompt = `Rewrite education details as concise bullet lines (degree, institution, year, honors if relevant). Do NOT include any explanations or section headers. Bullet lines only. Return JSON only per schema. Limit to ${characterLimit} characters.\n\nEducation:\n${currentText}`;
                break;
            case 'publications':
                prompt = `Rewrite publications as bullets: Title — Outlet, Year (or relevant details). Do NOT include any explanations or section headers. Bullet lines only. Return JSON only per schema. Limit to ${characterLimit} characters.\n\nPublications:\n${currentText}`;
                break;
            case 'coverLetter':
                // Determine safe company/role placeholders if missing to avoid hallucinated tech roles
                const hasJobText = (currentText.jobDescription || '').trim().length > 0;
                const companySafe = (currentText.companyName || '').trim() || (hasJobText ? '' : 'the company');
                const roleSafe = (currentText.targetRole || '').trim() || (hasJobText ? '' : 'the role');
                // Note: domain tailoring is enforced via prompt rules above.
                prompt = `Write a professional, personalized cover letter under 400 words mapped to the actual job text when provided. Use first person and align the user's experience/skills to the job requirements.

STRICT RULES:
- If Recipient Name exists, use it; else if Recipient Title exists, greet that title; else use 'Dear Hiring Manager'.
- If company or role are unknown and job text is present, do NOT invent them; refer neutrally (e.g., "your team" or "this role").
- Do not fabricate AI/engineering context if the job text suggests other domains. If the job text mentions warehouse/fulfillment/logistics, tailor content to that domain and avoid software/AI claims.
- Avoid placeholders and meta commentary.

Context:
Name: ${currentText.name}
Current Title: ${currentText.title}
Target Role: ${roleSafe}
Company: ${companySafe}
Job Description (primary job context if present): ${currentText.jobDescription || ''}
Job Post URL: ${currentText.jobPostUrl || ''}
Job Summary: ${currentText.jobSummary || ''}
Job Responsibilities: ${(Array.isArray(currentText.jobResponsibilities) ? currentText.jobResponsibilities.join('; ') : '')}
Job Requirements: ${(Array.isArray(currentText.jobRequirements) ? currentText.jobRequirements.join('; ') : '')}
Why Company/Role: ${currentText.whyCompany || ''}
Key Achievements (lines): ${currentText.keyAchievements || ''}
Top Skills: ${currentText.keyRoleSkills || ''}
Summary: ${currentText.summary}
Experience: ${currentText.experience}
Skills: ${currentText.skills}
Education: ${currentText.education}
Applicant Address: ${currentText.applicantAddress || ''}
Recipient Name: ${currentText.recipientName || ''}
Recipient Title: ${currentText.recipientJobTitle || ''}
Additional Notes: ${currentText.coverLetterBody || ''}`;
                break;
            default:
                prompt = `Rewrite and improve the ${field} section concisely with clear impact. Limit to ${characterLimit} characters.\n\nOriginal:\n${currentText}`;
        }

        updateStatus(`Generating ${field}...`);
    let generatedText = '';
        let retries = 0;
        const maxRetries = 3;

        const shortBackoff = async (ms) => new Promise(r => setTimeout(r, ms));
        do {
            try {
                const schema = schemaForField(field);
                const data = await GeminiService.callGeminiChat({
                    model: 'gemini-3.1-flash-lite',
                    messages: [
                        { role: 'system', content: SYSTEM_INSTRUCTION },
                        { role: 'user', content: prompt }
                    ],
                    max_tokens: targetTokens,
                    temperature: 0.1,
                    response_mime_type: 'application/json',
                    response_schema: schema,
                    actionType: field === 'coverLetter' ? 'cover_letter' : 'resume_edit'
                });

                const raw = (data && data.choices && data.choices[0] && data.choices[0].message && data.choices[0].message.content) ? data.choices[0].message.content : '';
                let obj = null;
                try {
                    if (typeof raw === 'string') {
                        obj = JSON.parse(raw);
                    } else if (raw && typeof raw === 'object') {
                        obj = raw;
                    }
                } catch (e) {
                    const preview = typeof raw === 'string' ? raw.slice(0,120) : JSON.stringify(raw||{}).slice(0,120);
                    console.warn('[GeminiService] JSON parse failed (generateText)', { field, rawPreview: preview, error: e?.message });
                }
                generatedText = formatFromJson(field, obj).trim();
                let sanitized = sanitizeForField(field, generatedText);
                const metaDetected = hasMeta(sanitized);
                const bulletFields = new Set(['experience', 'skills', 'education', 'publications']);
                const bulletCount = bulletFields.has(field) && sanitized ? sanitized.split(/\r?\n/).filter(Boolean).length : 0;
                const minBullets = field === 'experience' ? 2 : 3;

                if (!generatedText || !sanitized || metaDetected || (bulletFields.has(field) && bulletCount < minBullets)) {
                    if (bulletFields.has(field) && bulletCount < minBullets) console.warn(`[GeminiService] Too few bullets (${bulletCount}) for ${field}. Retrying...`);
                    if (metaDetected) console.warn(`[GeminiService] Meta commentary detected for ${field}. Retrying...`);
                    console.warn(`Empty ${field} content received. Retrying...`);
                    updateStatus(`Retrying ${field} generation...`);
                } else if (sanitized.length <= characterLimit) {
                    updateStatus(`${field} generation complete.`);
                    return sanitized;
                } else {
                    console.warn(`Generated text exceeds ${characterLimit} characters. Retrying...`);
                    updateStatus(`Retrying ${field} generation...`);
                }
            } catch (error) {
                console.error('Error generating text: ', error);
                throw error;
            }

            retries++;
            await shortBackoff(300 * retries);
        } while (((!generatedText) || generatedText.length > characterLimit) && retries < maxRetries);

    if (!generatedText) {
            updateStatus(`Using existing ${field} content due to empty AI response.`);
            return (currentText || '').toString().trim();
        }

    if (generatedText.length > characterLimit && field !== 'coverLetter') {
            console.warn(`Truncating text to ${characterLimit} characters after ${maxRetries} retries.`);
            generatedText = generatedText.slice(0, characterLimit);
        }

    updateStatus(`${field} generation complete.`);
    return sanitizeForField(field, generatedText);
    },

    validateAndAdjust: async (field, generatedText, baseCharacterLimit, updateStatus) => {
        console.log(`Validating and adjusting text for ${field}.`);

        const adjustedCharacterLimit = (contentLength, baseLimit) => {
            return contentLength > 2000 ? baseLimit - 50 : baseLimit;
        };

        const characterLimit = adjustedCharacterLimit(generatedText.length, baseCharacterLimit);

    let prompt = `Refine the ${field} content to be concise and impactful. Return JSON only that matches the response_schema exactly. Limit to ${characterLimit} characters for the content value(s).\n\nContent to refine:\n${generatedText}`;

        updateStatus(`Validating ${field}...`);

    let validatedText = '';
        let retries = 0;
        const maxRetries = 3;

        const shortBackoff = async (ms) => new Promise(r => setTimeout(r, ms));
        do {
            try {
                const schema = schemaForField(field);
                const data = await GeminiService.callGeminiChat({
                    model: 'gemini-3.1-flash-lite',
                    messages: [
                        { role: 'system', content: SYSTEM_INSTRUCTION },
                        { role: 'user', content: prompt }
                    ],
                    max_tokens: getTargetTokens(field),
                    temperature: 0.05,
                    response_mime_type: 'application/json',
                    response_schema: schema,
                    actionType: field === 'coverLetter' ? 'cover_letter' : 'resume_edit'
                });
                const raw = (data && data.choices && data.choices[0] && data.choices[0].message && data.choices[0].message.content) ? data.choices[0].message.content : '';
                let obj = null;
                try {
                    if (typeof raw === 'string') {
                        obj = JSON.parse(raw);
                    } else if (raw && typeof raw === 'object') {
                        obj = raw;
                    }
                } catch (e) {
                    const preview = typeof raw === 'string' ? raw.slice(0,120) : JSON.stringify(raw||{}).slice(0,120);
                    console.warn('[GeminiService] JSON parse failed (validateAndAdjust)', { field, rawPreview: preview, error: e?.message });
                }
                validatedText = formatFromJson(field, obj).trim();
                let sanitized = sanitizeForField(field, validatedText);
                const metaDetected = hasMeta(sanitized);
                const bulletFields = new Set(['experience', 'skills', 'education', 'publications']);
                const bulletCount = bulletFields.has(field) && sanitized ? sanitized.split(/\r?\n/).filter(Boolean).length : 0;
                const minBullets = field === 'experience' ? 2 : 3;

                if (!validatedText || !sanitized || metaDetected || (bulletFields.has(field) && bulletCount < minBullets)) {
                    if (bulletFields.has(field) && bulletCount < minBullets) console.warn(`[GeminiService] Too few bullets in validation (${bulletCount}) for ${field}. Retrying...`);
                    if (metaDetected) console.warn(`[GeminiService] Meta commentary detected in validation for ${field}. Retrying...`);
                    console.warn(`Empty validation output for ${field}. Retrying...`);
                    updateStatus(`Retrying ${field} validation...`);
                } else if (sanitized.length <= characterLimit) {
                    updateStatus(`${field} validation complete.`);
                    return sanitized;
                } else {
                    console.warn(`Validated text still exceeds ${characterLimit} characters. Retrying...`);
                    updateStatus(`Retrying ${field} validation...`);
                }
            } catch (error) {
                console.error('Error validating text: ', error);
                throw error;
            }
            retries++;
            await shortBackoff(300 * retries);
        } while (((!validatedText) || validatedText.length > characterLimit) && retries < maxRetries);

    if (!validatedText) {
            updateStatus(`Keeping previous ${field} content due to empty validation response.`);
            return (generatedText || '').toString().trim();
        }

    if (validatedText.length > characterLimit && field !== 'coverLetter') {
            console.warn(`Truncating text to ${characterLimit} characters after ${maxRetries} retries.`);
            validatedText = validatedText.slice(0, characterLimit);
        }

    updateStatus(`${field} validation complete.`);
    return sanitizeForField(field, validatedText);
    },

    extractRelevantContent: async (field, messageContent) => {
        try {
            const prompt = `Extract and refine only the content for the "${field}" section from the message below. Return JSON that matches the schema.\n\nMessage:\n${messageContent}`;
            const schema = schemaForField(field);
            const data = await GeminiService.callGeminiChat({
                model: 'gemini-3.1-flash-lite',
                messages: [
                    { role: 'system', content: SYSTEM_INSTRUCTION },
                    { role: 'user', content: prompt }
                ],
                max_tokens: 400,
                temperature: 0.15,
                response_mime_type: 'application/json',
                response_schema: schema,
                actionType: field === 'coverLetter' ? 'cover_letter' : 'resume_edit'
            });
            const raw = (data && data.choices && data.choices[0] && data.choices[0].message && data.choices[0].message.content) ? data.choices[0].message.content : '';
            let obj = null;
            try { obj = JSON.parse(raw); } catch (e) { console.warn('[GeminiService] JSON parse failed (extractRelevantContent)', { field, rawPreview: (raw||'').slice(0,120), error: e?.message }); }
            const extractedContent = sanitizeForField(field, formatFromJson(field, obj));
            console.log(`GeminiService - Extracted content for ${field}:`, extractedContent);
            return extractedContent;
        } catch (error) {
            console.error(`Error extracting content for ${field}:`, error);
            throw error;
        }
    },

    detectSectionLabels: async (messageContent) => {
        try {
            const prompt = `Identify section labels and their start/end character positions in the message. Consider: Summary, Experience, Skills, Education, Publications. Return JSON only.`;
            const response_schema = {
                type: 'OBJECT',
                properties: {
                    labels: {
                        type: 'ARRAY',
                        items: {
                            type: 'OBJECT',
                            properties: {
                                label: { type: 'STRING', description: 'Section label name' },
                                start: { type: 'INTEGER', description: 'Start char index' },
                                end: { type: 'INTEGER', description: 'End char index (exclusive)' }
                            },
                            required: ['label', 'start', 'end']
                        }
                    }
                },
                required: ['labels'],
                propertyOrdering: ['labels']
            };
            const data = await GeminiService.callGeminiChat({
                model: 'gemini-3.1-flash-lite',
                messages: [
                    { role: 'system', content: SYSTEM_INSTRUCTION },
                    { role: 'user', content: `${prompt}\n\nMessage:\n${messageContent}` }
                ],
                max_tokens: 220,
                temperature: 0.15,
                response_mime_type: 'application/json',
                response_schema,
                actionType: 'resume_edit'
            });
            const raw = (data && data.choices && data.choices[0] && data.choices[0].message && data.choices[0].message.content) ? data.choices[0].message.content : '';
            let obj = null;
            try { obj = JSON.parse(raw); } catch (e) { console.warn('[GeminiService] JSON parse failed (detectSectionLabels)', { rawPreview: (raw||'').slice(0,120), error: e?.message }); }
            console.log('Detected section labels (JSON):', obj);
            return obj;
        } catch (error) {
            console.error('Error detecting section labels:', error);
            throw error;
        }
    },
};

export default GeminiService;
export { sanitizeForField, hasMeta };
